import { hasOrthopulse } from '@odo/shared-ui/clinical/orthopulse';
import { tiptapDocFromString } from '../dx-report/editor/editorState';
import { EMPTY_TIPTAP_DOC } from '../dx-report/templates';
import { buildMergedSdiPdf } from '@odo/shared-ui/clinical-pdf/sdi/buildSdiPdf';
import { submitSdi, editSdi } from './sdiSubmit';
import {
    createSdiDraft,
    updateSdiDraft,
    deleteSdiDraft
} from '../../../../services/clinicalDrafts';
import { extractSdiFormDataFromEvent } from '@odo/shared-ui/clinical/eventReaders';
import { ALIGNER_WEAR_OPTIONS } from '@odo/shared-ui/clinical/alignerFields';
import type { ClinicalWizardConfig } from '../clinical/clinicalWizardConfig';
import type { SDIFormData, SectionToggles } from '@odo/shared-ui/clinical/types';

const DEFAULT_CADENCE_DAYS = '7';
const ORTHOPULSE_CADENCE_DAYS = '5';

function createDefaults(patientName: string, orthopulseStatus?: string | null): SDIFormData {
    const cadence = hasOrthopulse(orthopulseStatus)
        ? ORTHOPULSE_CADENCE_DAYS
        : DEFAULT_CADENCE_DAYS;
    return {
        date: new Date().toISOString().split('T')[0],
        patientName,
        notes: '',
        notesDoc: EMPTY_TIPTAP_DOC,
        elasticsSchemes: [],
        numberOfAligners: 'All',
        nextAppointmentAligner: '',
        alignerWear: 'Full Time',
        dmScanFrequency: cadence,
        alignerChangeFrequency: cadence
    };
}

function mergeWithDefaults(partial: Partial<SDIFormData>, defaults: SDIFormData): SDIFormData {
    const notesStr = partial.notes ?? defaults.notes;
    const notesDoc = partial.notesDoc ?? tiptapDocFromString(notesStr);
    return {
        date: partial.date ?? defaults.date,
        patientName: partial.patientName ?? defaults.patientName,
        notes: notesStr,
        notesDoc,
        elasticsSchemes: partial.elasticsSchemes ?? defaults.elasticsSchemes,
        legacyElastics: partial.legacyElastics ?? defaults.legacyElastics,
        numberOfAligners: partial.numberOfAligners ?? defaults.numberOfAligners,
        nextAppointmentAligner: partial.nextAppointmentAligner ?? defaults.nextAppointmentAligner,
        alignerWear: partial.alignerWear ?? defaults.alignerWear,
        dmScanFrequency: partial.dmScanFrequency ?? defaults.dmScanFrequency,
        alignerChangeFrequency: partial.alignerChangeFrequency ?? defaults.alignerChangeFrequency
    };
}

function isFormValid(data: SDIFormData): boolean {
    return data.date.trim() !== '' && data.patientName.trim() !== '';
}

function buildPdf(
    data: SDIFormData,
    attachment: File | null,
    hasOrthopulseFlag: boolean,
    toggles: SectionToggles,
    attachmentBytes?: Uint8Array | null
): Promise<Blob> {
    return buildMergedSdiPdf(data, attachment, hasOrthopulseFlag, toggles, attachmentBytes);
}

export const SDI_WIZARD_CONFIG: ClinicalWizardConfig<SDIFormData> = {
    kindLabel: 'SDI',
    virtualAppointmentTriggerSeries: 'current',
    successCopy: 'The start day instructions have been sent to the practice.',
    nextAppointmentLabel: 'Next Appointment Aligner',
    nextAppointmentPlaceholder: 'e.g., aligner 5',
    alignerWearOptions: ALIGNER_WEAR_OPTIONS,
    getNextAppointment: (data) => data.nextAppointmentAligner,
    setNextAppointment: (data, value) => ({ ...data, nextAppointmentAligner: value }),
    createDefaults,
    extractFormDataFromEvent: extractSdiFormDataFromEvent,
    mergeWithDefaults,
    isFormValid,
    buildPdf,
    submit: submitSdi,
    edit: editSdi,
    drafts: {
        createDraft: createSdiDraft,
        updateDraft: updateSdiDraft,
        deleteDraft: deleteSdiDraft
    },
    getElasticsSchemes: (data) => data.elasticsSchemes ?? [],
    setElasticsSchemes: (data, elasticsSchemes) => ({ ...data, elasticsSchemes })
};
